// =============================================================================
// Booking Requests Page - Admin dashboard per richieste di prenotazione pubblica
// =============================================================================

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Inbox, Loader2, Users } from 'lucide-react';
import {
  useBookingRequests,
  useApproveBookingRequest,
  useRejectBookingRequest,
  type BookingRequestListResponse,
} from '../api/hooks';
import { useTenantStore, useTenantTimeZone } from '../store/tenantStore';
import { useToast } from '../store/toastStore';
import { useDateLocale } from '../hooks/useDateLocale';
import { formatInTz } from '../lib/tenantTime';
import type { BookingRequestStatus } from '../types/public-booking';
import { BookingRequestDetailDrawer } from '../components/bookingRequests/BookingRequestDetailDrawer';

type StatusFilter = 'ALL' | BookingRequestStatus;

const STATUS_FILTERS: StatusFilter[] = ['PENDING', 'APPROVED', 'REJECTED', 'EXPIRED', 'ALL'];

type Row = BookingRequestListResponse['data'][number];

export function BookingRequestsPage() {
  const { t } = useTranslation('booking-requests');
  const activeRole = useTenantStore((s) => s.activeRole);
  const toast = useToast();
  const dateLocale = useDateLocale();
  const timeZone = useTenantTimeZone();

  if (activeRole && !['ADMIN', 'OWNER'].includes(activeRole)) {
    return <Navigate to="/scheduler" replace />;
  }

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      status: statusFilter === 'ALL' ? undefined : [statusFilter],
      limit: 100,
    }),
    [statusFilter],
  );

  const { data, isLoading, refetch } = useBookingRequests(query);
  const approveMutation = useApproveBookingRequest();
  const rejectMutation = useRejectBookingRequest();

  const rows: Row[] = data?.data ?? [];

  const handleApprove = async (
    id: string,
    opts?: { reviewNotes?: string; approveAs?: 'join' | 'new_appointment' },
  ) => {
    try {
      await approveMutation.mutateAsync({
        id,
        payload: { reviewNotes: opts?.reviewNotes, approveAs: opts?.approveAs },
      });
      toast.success(t('toast.approved'));
      setSelectedId(null);
      refetch();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('toast.approveError');
      toast.error(msg);
    }
  };

  const handleReject = async (
    id: string,
    payload: { rejectionReason?: string; reviewNotes?: string },
  ) => {
    try {
      await rejectMutation.mutateAsync({ id, payload });
      toast.success(t('toast.rejected'));
      setSelectedId(null);
      refetch();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('toast.rejectError');
      toast.error(msg);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-emerald-600" />
              {t('title')}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {STATUS_FILTERS.map((s) => {
              const active = statusFilter === s;
              const labelKey =
                s === 'ALL'
                  ? 'filters.all'
                  : s === 'PENDING'
                    ? 'filters.pending'
                    : s === 'APPROVED'
                      ? 'filters.approved'
                      : s === 'REJECTED'
                        ? 'filters.rejected'
                        : 'filters.expired';
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">{t('columns.service')}</th>
                  <th className="px-4 py-3">{t('columns.customer')}</th>
                  <th className="px-4 py-3">{t('columns.requestedFor')}</th>
                  <th className="px-4 py-3">{t('columns.match')}</th>
                  <th className="px-4 py-3">{t('columns.submitted')}</th>
                  <th className="px-4 py-3">{t('columns.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const snapshot = (row.customerSnapshot ?? {}) as {
                    firstName?: string;
                    lastName?: string;
                    email?: string;
                  };
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {row.serviceName}
                        {row.isJoinRequest && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 align-middle">
                            <Users className="w-3 h-3" />
                            {t('joinRequestBadge')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div>{snapshot.firstName} {snapshot.lastName}</div>
                        {snapshot.email && (
                          <div className="text-xs text-gray-500">{snapshot.email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {formatInTz(new Date(row.requestedStart), timeZone, 'PPp', dateLocale)}
                      </td>
                      <td className="px-4 py-3">
                        <MatchBadge confidence={row.matchConfidence} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {format(new Date(row.createdAt), 'PP p', { locale: dateLocale })}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedId && (
        <BookingRequestDetailDrawer
          requestId={selectedId}
          onClose={() => setSelectedId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          isSubmitting={approveMutation.isPending || rejectMutation.isPending}
        />
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation('booking-requests');
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Inbox className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900">{t('empty.title')}</h3>
      <p className="text-sm text-gray-500 mt-1 max-w-md">{t('empty.description')}</p>
    </div>
  );
}

function MatchBadge({ confidence }: { confidence: string | null }) {
  const { t } = useTranslation('booking-requests');
  const key = confidence ?? 'none';
  const className =
    confidence === null
      ? 'bg-amber-50 text-amber-700 border border-amber-200'
      : confidence === 'fuzzy_identity'
        ? 'bg-blue-50 text-blue-700 border border-blue-200'
        : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {t(`matchLabel.${key}`)}
    </span>
  );
}

function StatusBadge({ status }: { status: BookingRequestStatus }) {
  const map: Record<BookingRequestStatus, string> = {
    PENDING: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    REJECTED: 'bg-red-50 text-red-700 border-red-200',
    EXPIRED: 'bg-gray-100 text-gray-600 border-gray-200',
    CANCELLED_BY_CUSTOMER: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${map[status]}`}>
      {status}
    </span>
  );
}

export default BookingRequestsPage;
